import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/#packages", label: "Packages" },
  { to: "/#craft", label: "Our Craft" },
  { to: "/#reviews", label: "Reviews" },
  { to: "/#gallery", label: "Gallery" },
  { to: "/gift-cards", label: "Gift Cards" },
  { to: "/#contact", label: "Contact" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.querySelector(to.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo(0, 0);
    }
  };

  return (
    <header data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#09090B]/85">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => go("/")} data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-10 h-10 border border-amber-400/70 rotate-45 flex items-center justify-center transition-colors group-hover:bg-amber-500/10">
            <span className="-rotate-45 font-serif-lux font-semibold text-amber-400 text-xl leading-none">V</span>
          </span>
          <span className="text-left leading-none">
            <span className="font-serif-lux text-xl text-zinc-100 block">Valet Premier</span>
            <span className="text-[9px] uppercase tracking-[0.45em] text-amber-400/90 block mt-1">Detailing</span>
          </span>
        </button>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.to)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => go(user ? "/account" : "/login")}
            data-testid="nav-account-button"
            className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 transition-colors"
          >
            <User size={13} /> {user ? "Account" : "Sign In"}
          </button>
          <button
            onClick={() => go("/book")}
            data-testid="nav-book-now-button"
            className="gold-foil text-black text-[11px] font-semibold uppercase tracking-[0.25em] px-6 py-3 rounded-sm hover:opacity-90 transition-opacity"
          >
            Book Now
          </button>
        </div>
        <button className="md:hidden text-zinc-300" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>
      <div className="h-[3px] bg-gradient-to-r from-amber-500/10 via-amber-400 to-amber-500/10" />
      {open && (
        <div className="md:hidden border-t border-amber-500/10 bg-[#09090B]/95 backdrop-blur-xl px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <button key={l.label} onClick={() => go(l.to)} data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-left text-sm uppercase tracking-[0.2em] text-zinc-300">
              {l.label}
            </button>
          ))}
          <button onClick={() => go(user ? "/account" : "/login")} data-testid="nav-mobile-account" className="text-left text-sm uppercase tracking-[0.2em] text-zinc-300">
            {user ? "My Account" : "Sign In"}
          </button>
          <button onClick={() => go("/book")} data-testid="nav-mobile-book" className="gold-foil text-black text-sm font-semibold uppercase tracking-[0.2em] px-5 py-3 rounded-sm text-center">
            Book Now
          </button>
        </div>
      )}
    </header>
  );
}
